import React, { useState } from 'react';
import {
    Paper,
    Box,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Grid,
    useTheme,
    Chip,
} from '@mui/material';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import {
    ShowChart,
    AttachMoney,
    ElectricBolt,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    formatCurrency,
    formatKwh,
    formatWatt,
    buildTariffBreakdownData,
} from '../../utils/powerCostUtils';
import { getMonthName } from '../../utils/monthlyDataUtils';

function AnnualChartTooltip({ active, payload, label, t, selectedYear }) {
    const theme = useTheme();
    if (!active || !payload || payload.length === 0) return null;

    const isDark = theme.palette.mode === 'dark';
    const dataPoint = payload[0]?.payload;

    return (
        <Box
            sx={{
                bgcolor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1.5,
                boxShadow: 4,
                minWidth: 200,
                backdropFilter: 'blur(4px)',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {label} {selectedYear}
                </Typography>
                {dataPoint?.isPartial && (
                    <Chip
                        label={t('common.cyclePartial', 'In Progress')}
                        size="small"
                        color="warning"
                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                    />
                )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {payload.map((entry, index) => {
                    if (entry.value === null || entry.value === undefined) return null;

                    let displayVal = entry.value;
                    if (entry.dataKey === 'totalCost' || entry.dataKey === 'baseEnergyCost') {
                        displayVal = formatCurrency(entry.value);
                    } else if (entry.dataKey === 'totalKwh') {
                        displayVal = formatKwh(entry.value);
                    } else if (entry.dataKey === 'averagePowerWatt') {
                        displayVal = formatWatt(entry.value);
                    }

                    return (
                        <Box
                            key={`item-${index}`}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                fontSize: '0.8125rem',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: entry.color || entry.fill,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {entry.name}
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {displayVal}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function TariffPieTooltip({ active, payload, t }) {
    const theme = useTheme();
    if (!active || !payload || payload.length === 0) return null;

    const isDark = theme.palette.mode === 'dark';
    const data = payload[0]?.payload;

    return (
        <Box
            sx={{
                bgcolor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1.5,
                boxShadow: 4,
                minWidth: 160,
                backdropFilter: 'blur(4px)',
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: data?.color }}>
                {data?.name}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 800 }}>
                {formatCurrency(data?.cost)}
            </Typography>
            {data?.units && (
                <Typography variant="caption" color="text.secondary" display="block">
                    {formatKwh(data.units)} {data.rate ? `@ ${data.rate} ฿/kWh` : ''}
                </Typography>
            )}
        </Box>
    );
}

function PowerCostCharts({
    annualData = [],
    activeRecord,
    selectedYear,
    selectedMonth,
}) {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [viewMode, setViewMode] = useState('cost'); // 'cost' | 'kwh' | 'combo'

    // Tariff breakdown data for the active/selected record
    const tariffBreakdown = buildTariffBreakdownData(activeRecord, t);

    return (
        <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Chart 1: Annual Trend (Bar + Line Combo) */}
            <Grid size={{ xs: 12, lg: 8 }}>
                <Paper
                    elevation={2}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        height: '100%',
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 2.5,
                        }}
                    >
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {t('common.annualCostTrend', 'Annual Electricity Cost Trend')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t('common.year', 'Year')}: {selectedYear}
                            </Typography>
                        </Box>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => {
                                if (val !== null) setViewMode(val);
                            }}
                            size="small"
                        >
                            <ToggleButton value="cost" sx={{ textTransform: 'none', px: 1.25, py: 0.5 }}>
                                <AttachMoney fontSize="small" sx={{ mr: 0.5 }} />
                                {t('common.costInBaht', 'Cost (฿)')}
                            </ToggleButton>
                            <ToggleButton value="kwh" sx={{ textTransform: 'none', px: 1.25, py: 0.5 }}>
                                <ElectricBolt fontSize="small" sx={{ mr: 0.5 }} />
                                {t('common.unitsInKwh', 'kWh')}
                            </ToggleButton>
                            <ToggleButton value="combo" sx={{ textTransform: 'none', px: 1.25, py: 0.5 }}>
                                <ShowChart fontSize="small" sx={{ mr: 0.5 }} />
                                {t('common.costVsKwh', 'Cost + Power')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Box sx={{ width: '100%', height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart
                                data={annualData}
                                margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}
                                />
                                <XAxis
                                    dataKey="monthShort"
                                    stroke={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'}
                                    tick={{ fontSize: 12 }}
                                />

                                {/* Primary Y-Axis */}
                                <YAxis
                                    yAxisId="left"
                                    stroke={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'}
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(v) =>
                                        viewMode === 'kwh' ? `${v} kWh` : `฿${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`
                                    }
                                />

                                {/* Secondary Y-Axis for Power (Watt) in combo view */}
                                {viewMode === 'combo' && (
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        stroke="#8b5cf6"
                                        tick={{ fontSize: 11 }}
                                        tickFormatter={(v) => `${v} W`}
                                    />
                                )}

                                <Tooltip
                                    content={<AnnualChartTooltip t={t} selectedYear={selectedYear} />}
                                />

                                <Legend
                                    wrapperStyle={{ paddingTop: 10 }}
                                    formatter={(value) => (
                                        <span style={{ color: isDark ? '#fff' : '#333', fontSize: 12, fontWeight: 500 }}>
                                            {value}
                                        </span>
                                    )}
                                />

                                {/* Bars for Cost or kWh */}
                                {viewMode === 'cost' && (
                                    <Bar
                                        yAxisId="left"
                                        dataKey="totalCost"
                                        name={t('common.totalCost', 'Total Cost (฿)')}
                                        fill="#10b981"
                                        radius={[4, 4, 0, 0]}
                                        maxBarSize={45}
                                    />
                                )}

                                {viewMode === 'kwh' && (
                                    <Bar
                                        yAxisId="left"
                                        dataKey="totalKwh"
                                        name={t('common.totalUnitsConsumed', 'Consumption (kWh)')}
                                        fill="#3b82f6"
                                        radius={[4, 4, 0, 0]}
                                        maxBarSize={45}
                                    />
                                )}

                                {viewMode === 'combo' && (
                                    <>
                                        <Bar
                                            yAxisId="left"
                                            dataKey="totalCost"
                                            name={t('common.totalCost', 'Total Cost (฿)')}
                                            fill="#10b981"
                                            radius={[4, 4, 0, 0]}
                                            maxBarSize={40}
                                        />
                                        <Line
                                            yAxisId="right"
                                            type="monotone"
                                            dataKey="averagePowerWatt"
                                            name={t('common.averagePower', 'Average Power (W)')}
                                            stroke="#8b5cf6"
                                            strokeWidth={2.5}
                                            dot={{ r: 4, fill: '#8b5cf6' }}
                                            activeDot={{ r: 6 }}
                                            connectNulls
                                        />
                                    </>
                                )}
                            </ComposedChart>
                        </ResponsiveContainer>
                    </Box>
                </Paper>
            </Grid>

            {/* Chart 2: Tiered Tariff Breakdown */}
            <Grid size={{ xs: 12, lg: 4 }}>
                <Paper
                    elevation={2}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        height: '100%',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s ease-in-out',
                    }}
                >
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {t('common.tieredTariffBreakdown', 'Tiered Tariff Breakdown')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {activeRecord ? `${getMonthName(activeRecord.month, t)} ${selectedYear}` : selectedYear}
                        </Typography>
                    </Box>

                    {tariffBreakdown.length > 0 ? (
                        <>
                            <Box sx={{ width: '100%', height: 210, position: 'relative' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={tariffBreakdown}
                                            dataKey="cost"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={52}
                                            outerRadius={82}
                                            paddingAngle={3}
                                        >
                                            {tariffBreakdown.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<TariffPieTooltip t={t} />} />
                                    </PieChart>
                                </ResponsiveContainer>

                                {/* Center Donut Text */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        textAlign: 'center',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {t('common.totalCost', 'Total')}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#10b981' }}>
                                        {formatCurrency(activeRecord?.totalCost)}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Breakdown List */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
                                {tariffBreakdown.map((item, idx) => (
                                    <Box
                                        key={`tariff-item-${idx}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            fontSize: '0.8125rem',
                                            p: 0.5,
                                            borderRadius: 1,
                                            '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
                                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                {item.name}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                            {formatCurrency(item.cost)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 250 }}>
                            <Typography variant="body2" color="text.secondary">
                                {t('common.noData', 'No tariff data available')}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Grid>
        </Grid>
    );
}

export default PowerCostCharts;
